import { Box, Container, Flex } from "theme-ui";
import React, { memo, ReactNode, useState } from "react";
import { keyframes } from "@emotion/react";
import { Logo } from "../../components/Logo";
import { Link, useStaticQuery, graphql } from "gatsby";
import { Icon } from "../../components/Icons";
import { useSecureLinks } from "../../hooks/useSecureLinks";
import { StaticImage } from "gatsby-plugin-image";

const growY = keyframes`
from {
  transform: scaleY(0);
}
to {
  transform: scaleY(1);
}
`;

export interface AffiliateHeaderProps {
  cta?: JSX.Element | string;
  partnerLogo?: JSX.Element;
}

interface LoginRowItemProps {
  /** proportional width */
  fr: number;
  children: ReactNode;
}

const LoginRowItem: React.FC<LoginRowItemProps> = (props) => {
  return (
    <div
      sx={{
        flexBasis: `${props.fr * 100}%`,
        flexGrow: 1,
        flexShrink: 0,
        borderRightWidth: 1,
        borderRightColor: "#333333",
        borderRightStyle: "solid",
        borderLeftWidth: 1,
        borderLeftColor: "#767676",
        borderLeftStyle: "solid",
        "&:last-of-type": {
          borderRight: "none",
        },
        "&:first-of-type": {
          borderLeft: "none",
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        py: 2,
        px: 0,
      }}
    >
      {props.children}
    </div>
  );
};

/**
 * Provides useful navigation links.
 */
export const AffiliateHeader: React.FC<AffiliateHeaderProps> = (props) => {
  const { cta, partnerLogo } = props;
  const secureLinks = useSecureLinks();
  return (
    <Box
      as="header"
      sx={{
        flexDirection: "column",
        fontFamily: "body",
        boxShadow: "rgb(135, 135, 135) 0px 1px 2px 0px",
        zIndex: 2000,
        display: "flex",
        height: [92, null, null, "83px"],
        position: "sticky",
        borderBottom: "5px solid #645B88",
        bg: "#fff",
        borderBottomWidth: "5px",
        borderImage:
          "linear-gradient(to right, #645B88, #2E368F, #42368E, #D91828, #FFDD00) 1 stretch",
        alignItems: "baseline",

        top: 0,
      }}
      id="Site-Nav-Header"
    >
      <Flex
        as="nav"
        sx={{
          boxSizing: "border-box",
          minWidth: 0,
          width: "100%",
          bg: "#fff",
          display: "flex",
          alignItems: "center",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
        aria-label="Main Menu"
      >
        <Container
          sx={{
            px: "15px",
            pb: 0,
            display: "flex",
            alignItems: "center",
            pt: [0, null, null, 0],
            justifyContent: "space-between",
            maxWidth: [null, null, "100%", "1360px"],
          }}
        >
          <Flex
            sx={{
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {partnerLogo && (
              <>
                <a href="/" sx={{ width: 80 }}>
                  {partnerLogo}
                </a>{" "}
                <span
                  sx={{
                    fontSize: 40,
                  }}
                >
                  +
                </span>
              </>
            )}
            <Link
              to="/"
              sx={{
                lineHeight: 0,
                ml: partnerLogo ? 3 : 0,
              }}
            >
              <Logo
                sx={{
                  width: [180, null, null, 220],
                  height: 35,
                  display: ["block"],
                  "&:focus": {
                    outline: "1px solid #1A1818",
                    border: "1px solid #1A1818",
                  },
                }}
              />
              {/* <StaticImage
                src="../../images/logoMobile.png"
                sx={{
                  display: ["block", null, null, "none"],
                }}
                placeholder="none"
                alt="Cash Money logo"
              /> */}
            </Link>
          </Flex>
          <Flex>
            {cta || (
              <Flex
                aria-labelledby="main-menu-heading"
                aria-label="Main Menu"
                sx={{
                  //alignItems: 'center',
                  listStyle: "none",
                  minHeight: "100%",
                  paddingLeft: 0,
                  flexDirection: ["column", "unset"],
                  alignItems: ["flex-start", "center"],
                }}
              >
                <Flex
                  sx={{
                    gap: "5px",
                    width: ["auto", 180],
                  }}
                >
                  <Icon
                    name={"lock-outlined"}
                    width={20}
                    height={25}
                    alt={"Login"}
                    viewBox={"0 0 20 25"}
                    sx={{
                      mr: 1,
                      fill: "none",
                      stroke: "#000",
                      strokeLinecap: "round",
                      strokeLinejoin: "round",
                      strokeWidth: "1.5",
                      g: { fill: "#fff" },
                      "&:hover": {
                        fill: "primary",
                      },
                    }}
                  />
                  <a
                    href={secureLinks.login}
                    sx={{
                      fontWeight: "700",
                      "&:hover": { color: "primary" },
                    }}
                  >
                    Log In
                  </a>
                </Flex>
                <a
                  href={secureLinks.loanApplication}
                  sx={{
                    variant: "buttons.newPrimary",
                    ml: "25px",
                    py: "5px",
                    display: ["none", null, "block"],
                  }}
                >
                  Apply Now
                </a>
              </Flex>
            )}
          </Flex>
        </Container>
      </Flex>
    </Box>
  );
};
export default memo(AffiliateHeader);
